import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { NotificationFormProps } from './types';
import ImageUpload from '../../../../../../common/components/xepp/image-upload/ImageUpload';
import NotificationList, { NotificationListItem } from '../notification-list/NotificationList';
import AddButtonSection, { ADD_BUTTON_DEFAULTS, AddButtonFields } from './AddButtonSection';
import ConfirmationModal from '../../../../../../common/components/xepp/confirmation-modal/ConfirmationModal';
import styles from './NotificationForm.module.scss';
import { Input } from '../../../../../customer/components';
import ToggleSlider from '../../../../../../common/components/xepp/toggle-slider/ToggleSlider';
import { useLazyGetBroadcastNotificationsByTypeQuery, useDeleteBroadcastNotificationMutation } from '../../../../../../service/notification-service';
import { getErrorMessage } from '../../../../../../utils/error-handler';
import { toast } from 'react-toastify';
import { BroadcastNotificationSubType } from '../../../../../../model/notifications/notifications';
import { TrashCanIcon, TrashFillIcon } from '../../../../../../assets/icons';
import CircularProgressIndicator from '../../../../../../common/components/circular-progress-indicator/CircularProgressIndicator';

interface ListNotificationFormValues extends AddButtonFields {
    title: string;
    message: string;
    body: string;
    addImage: boolean;
    image: File | null;
    sendViaEmail: boolean;
}

export const LIST_TABS = ['New Notification', 'Manage Notification'] as const;
export type ListTab = (typeof LIST_TABS)[number];

interface ListNotificationFormProps extends NotificationFormProps {
    activeTab?: ListTab;
}

const ListNotificationForm = ({ onSubmit, activeTab = 'New Notification' }: ListNotificationFormProps) => {
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [pendingData, setPendingData] = useState<ListNotificationFormValues | null>(null);
    const [pendingDeleteId, setPendingDeleteId] = useState<string | number | null>(null);

    const [fetchNotifications, { data: rawNotifications, isLoading: notificationsLoading }] = useLazyGetBroadcastNotificationsByTypeQuery();
    const [deleteBroadcastNotification] = useDeleteBroadcastNotificationMutation();
    const [pendingDeleteGroupId, setPendingDeleteGroupId] = useState<string | null>(null);

    useEffect(() => {
        if (activeTab === 'Manage Notification') {
            fetchNotifications({ type: BroadcastNotificationSubType.ListNotification });
        } else {
            reset();
        }
    }, [activeTab]);

    const notifications: NotificationListItem[] = (rawNotifications ?? [])
        .map(n => ({ id: n.id, groupId: n.groupId, title: n.title, message: n.message, createdAt: n.createdAt }));

    const handleDelete = (id: string | number) => {
        const notification = notifications.find(n => n.id === id);
        setPendingDeleteGroupId(notification?.groupId ?? null);
        setPendingDeleteId(id);
    };

    const handleConfirmDelete = async () => {
        if (pendingDeleteId !== null) {
            try {
                if (pendingDeleteGroupId) {
                    await deleteBroadcastNotification({ notificationId: pendingDeleteGroupId }).unwrap();
                    toast.success('Notification deleted successfully');
                }
            } catch (e: any) {
                toast.error(getErrorMessage(e?.data));
            }
        }
        setPendingDeleteId(null);
        setPendingDeleteGroupId(null);
    };

    const { control, handleSubmit, watch, reset } = useForm<ListNotificationFormValues>({
        defaultValues: {
            title: '',
            message: '',
            body: '',
            ...ADD_BUTTON_DEFAULTS,
            addImage: false,
            image: null,
            sendViaEmail: false,
        },
    });

    const addImage = watch('addImage');

    const onFormSubmit = (data: ListNotificationFormValues) => {
        setPendingData(data);
        setIsConfirmOpen(true);
    };

    const handleConfirm = () => {
        if (!pendingData) return;
        setIsConfirmOpen(false);
        onSubmit({ notificationType: 'list', ...pendingData });
        setPendingData(null);
    };

    return (
        <div>
            {activeTab === 'New Notification' && (
                <form id="notification-form" className={styles.form} onSubmit={handleSubmit(onFormSubmit)}>
                    <Controller
                        name="title"
                        control={control}
                        rules={{ required: 'Notification title is required.', maxLength: { value: 100, message: 'Title must not exceed 100 characters.' } }}
                        render={({ field, fieldState }) => (
                            <div className={styles.field}>
                                <div className={styles.labelRow}>
                                    <label className={styles.label}>Title</label>
                                    <span className={styles.charCount}>{field.value.length} / 100</span>
                                </div>
                                <Input
                                    variant="light"
                                    value={field.value}
                                    onChange={field.onChange}
                                    placeholder=""
                                    error={fieldState.error?.message}
                                />
                            </div>
                        )}
                    />
                    <Controller
                        name="message"
                        control={control}
                        rules={{ required: 'Message is required.', maxLength: { value: 200, message: 'Message must not exceed 200 characters.' } }}
                        render={({ field, fieldState }) => (
                            <div className={styles.field}>
                                <div className={styles.labelRow}>
                                    <label className={styles.label}>Message</label>
                                    <span className={styles.charCount}>{field.value.length} / 200</span>
                                </div>
                                <textarea
                                    className={`${styles.textarea} ${fieldState.error ? styles.textareaError : ''}`}
                                    value={field.value}
                                    onChange={field.onChange}
                                    placeholder=""
                                    rows={4}
                                />
                                {fieldState.error && <span className={styles.errorText}>{fieldState.error.message}</span>}
                            </div>
                        )}
                    />
                    <Controller
                        name="body"
                        control={control}
                        rules={{ required: 'Notification body is required.', maxLength: { value: 200, message: 'Body must not exceed 200 characters.' } }}
                        render={({ field, fieldState }) => (
                            <div className={styles.field}>
                                <div className={styles.labelRow}>
                                    <label className={styles.label}>Body</label>
                                    <span className={styles.charCount}>{field.value.length} / 200</span>
                                </div>
                                <textarea
                                    className={`${styles.textarea} ${fieldState.error ? styles.textareaError : ''}`}
                                    value={field.value}
                                    onChange={field.onChange}
                                    placeholder=""
                                    rows={4}
                                />
                                {fieldState.error && <span className={styles.errorText}>{fieldState.error.message}</span>}
                            </div>
                        )}
                    />
                    <AddButtonSection control={control as any} />
                    <Controller
                        name="addImage"
                        control={control}
                        render={({ field }) => (
                            <div className={styles.toggleRow}>
                                <div className={styles.toggleRowMain}>
                                    <span className={styles.toggleLabel}>Add Image</span>
                                    <ToggleSlider isActive={field.value} onChange={field.onChange} />
                                </div>
                                {addImage && (
                                    <Controller
                                        name="image"
                                        control={control}
                                        rules={{ validate: (value) => !addImage || !!value || 'Image is required' }}
                                        render={({ field: imageField, fieldState: imageFieldState }) => (
                                            <div className={styles.field}>
                                                <ImageUpload onChange={(file) => imageField.onChange(file)} />
                                                {imageFieldState.error && (
                                                    <span className={styles.errorText}>{imageFieldState.error.message}</span>
                                                )}
                                            </div>
                                        )}
                                    />
                                )}
                            </div>
                        )}
                    />
                    <Controller
                        name="sendViaEmail"
                        control={control}
                        render={({ field }) => (
                            <div className={styles.toggleRow}>
                                <div className={styles.toggleRowMain}>
                                    <span className={styles.toggleLabel}>Send via Email</span>
                                    <ToggleSlider isActive={field.value} onChange={field.onChange} />
                                </div>
                            </div>
                        )}
                    />
                </form>
            )}

            {activeTab === 'Manage Notification' && (
                notificationsLoading
                    ? <div className={styles.loaderWrapper}><CircularProgressIndicator width={24} height={24} /></div>
                    : <NotificationList notifications={notifications} onDelete={handleDelete} />
            )}
            <ConfirmationModal
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={handleConfirm}
                title="Send List Notification"
                message="Send list notification to all customers?"
                confirmText="Confirm"
                cancelText="Cancel"
                variant="info"
            />
            <ConfirmationModal
                isOpen={pendingDeleteId !== null}
                onClose={() => setPendingDeleteId(null)}
                onConfirm={handleConfirmDelete}
                title="Delete list notification"
                message="Are you sure you want to delete this notification? Once it's gone, you can't undo it."
                confirmText="Yes Delete"
                cancelText="Cancel"
                variant="danger"
                subtitle='Remove notification'
                icon={<TrashFillIcon className={styles.trashIcon}/>}
            />
        </div>
    );
};

export default ListNotificationForm;
